import {
  fractionToAmountCents,
  roundToWireDecimals,
} from '@utils/allocationUnits';
import { allocationCellKey } from './allocationCellKey';

// Locks and solver output are whole cents, so the cap is compared in cents:
// a value that rounds to the cap's cents sits at the cap, not over it.
export const exceedsCapacityCents = (
  requestedCents: number,
  capacityFraction: number,
  mvCents: number,
) => requestedCents > fractionToAmountCents(capacityFraction, mvCents);

// A $0 account has no cents, so its cells are held to the cap as fractions.
export const exceedsCapacity = ({
  fraction,
  amountCents,
  capacityFraction,
  mvCents,
}: {
  fraction: number;
  amountCents?: number;
  capacityFraction: number;
  mvCents: number;
}) =>
  mvCents > 0
    ? exceedsCapacityCents(
        amountCents ?? fractionToAmountCents(fraction, mvCents),
        capacityFraction,
        mvCents,
      )
    : roundToWireDecimals(fraction) > roundToWireDecimals(capacityFraction);

export const capacityViolationKeys = ({
  cells,
  members,
  capacityFractionByKey,
}: {
  cells: { sa: string; fp: string; fraction: number }[];
  members: { sa: string; mvCents: number }[];
  capacityFractionByKey?: ReadonlyMap<string, number>;
}) => {
  const violationKeys = new Set<string>();
  if (!capacityFractionByKey) return violationKeys;

  const mvCentsBySa = new Map(members.map((m) => [m.sa, m.mvCents]));
  for (const cell of cells) {
    const key = allocationCellKey(cell.sa, cell.fp);
    const capacity = capacityFractionByKey.get(key);
    if (capacity === undefined) continue;

    const mvCents = mvCentsBySa.get(cell.sa) ?? 0;
    if (
      exceedsCapacity({
        fraction: cell.fraction,
        capacityFraction: capacity,
        mvCents,
      })
    ) {
      violationKeys.add(key);
    }
  }
  return violationKeys;
};
